/**
 * Loading Component
 * Provides engaging loading states with audio waveform animation and quotes
 */

// Podcast-themed loading quotes
const LOADING_QUOTES = [
    "Tuning in...",
    "Adjusting the frequency...",
    "Warming up the speakers...",
    "Cuing the podcast...",
    "Spinning up the audio...",
    "Finding the right wavelength...",
    "Buffering brilliance...",
    "Loading sonic goodness...",
    "Preparing your ears...",
    "Fetching audio wisdom...",
    "Syncing the soundwaves...",
    "Queuing the content...",
    "Dialing in...",
    "Almost there..."
];

// AI-specific thinking phrases for chat
const AI_THINKING_PHRASES = [
    "Pondering the podcast...",
    "Analyzing the transcript...",
    "Connecting the dots...",
    "Formulating thoughts...",
    "Processing audio wisdom...",
    "Synthesizing insights...",
    "Contemplating context...",
    "Gathering my thoughts...",
    "Parsing the conversation...",
    "Thinking deeply..."
];

/**
 * Create the audio waveform element
 * @returns {HTMLElement}
 */
function createWaveformElement() {
    const wave = document.createElement('span');
    wave.className = 'audio-wave';
    for (let i = 0; i < 5; i++) {
        wave.appendChild(document.createElement('span'));
    }
    return wave;
}

/**
 * Get a random quote from the list
 */
function getRandomQuote(quotes = LOADING_QUOTES) {
    return quotes[Math.floor(Math.random() * quotes.length)];
}

/**
 * Create a full loading container with waveform and quote
 * @param {string} context - 'default', 'chat', 'search', etc.
 * @returns {HTMLElement}
 */
export function createLoadingElement(context = 'default') {
    const quotes = context === 'chat' ? AI_THINKING_PHRASES : LOADING_QUOTES;

    const container = document.createElement('div');
    container.className = 'loading-container';

    container.appendChild(createWaveformElement());

    const quoteSpan = document.createElement('span');
    quoteSpan.className = 'loading-quote';
    quoteSpan.textContent = getRandomQuote(quotes);
    container.appendChild(quoteSpan);

    return container;
}

/**
 * Create an inline loading element (compact)
 * @param {string} text - Optional text to show
 * @returns {HTMLElement}
 */
export function createInlineLoadingElement(text = null) {
    const container = document.createElement('span');
    container.className = 'loading-inline';

    container.appendChild(createWaveformElement());

    const textSpan = document.createElement('span');
    textSpan.textContent = text || getRandomQuote();
    container.appendChild(textSpan);

    return container;
}

/**
 * Start rotating quotes in a loading element
 * @param {HTMLElement} quoteElement - The .loading-quote element
 * @param {string[]} quotes - Array of quotes to rotate through
 * @returns {number} Interval ID (use to stop rotation)
 */
export function startQuoteRotation(quoteElement, quotes = LOADING_QUOTES) {
    return setInterval(() => {
        quoteElement.style.opacity = '0';
        setTimeout(() => {
            quoteElement.textContent = getRandomQuote(quotes);
            quoteElement.style.opacity = '1';
        }, 300);
    }, 3000);
}

/**
 * Create a chat thinking message with rotating phrases
 * @returns {object} { element, intervalId }
 */
export function createChatThinkingElement() {
    const container = document.createElement('div');
    container.className = 'chat-message assistant thinking';

    const inline = document.createElement('span');
    inline.className = 'loading-inline';

    inline.appendChild(createWaveformElement());

    const textSpan = document.createElement('span');
    textSpan.className = 'thinking-text';
    textSpan.textContent = getRandomQuote(AI_THINKING_PHRASES);
    inline.appendChild(textSpan);

    container.appendChild(inline);

    // Rotate phrases every 2.5 seconds
    const intervalId = setInterval(() => {
        textSpan.style.opacity = '0.5';
        setTimeout(() => {
            textSpan.textContent = getRandomQuote(AI_THINKING_PHRASES);
            textSpan.style.opacity = '1';
        }, 200);
    }, 2500);

    return { element: container, intervalId };
}

// Export quotes and helper for external use
export { LOADING_QUOTES, AI_THINKING_PHRASES, createWaveformElement };
